/*
 * basn5-rx.js — canonical Rx line-item-property serializer
 * ----------------------------------------------------------------------------
 * BYTE-EXACT to Tajima Direct's shipped basn5 order data. This is the single
 * source of truth for the "Type in Rx" string so the new basn5 store produces
 * order data identical to TD — the existing Make.com + Airtable mappings must
 * work with zero remapping (the project's one hard constraint).
 *
 * Logic ported verbatim from:
 *   tajima-direct-shopify/scripts/basn5-cart-snippet.js
 *     (basn5FmtDiopter / basn5FmtAxis / basn5SerializeRx)
 * Validated against:
 *   tajima-direct-shopify/scripts/bcpo-property-keys.json -> typed_rx_format
 *
 * Loads in the browser (window.BASN5Rx) AND in Node (module.exports) on purpose
 * so the parity test in dev/parity/ exercises the SAME code the storefront runs.
 * Do not fork this logic into the cart code — the cart bridge must call BASN5Rx.
 *
 * Format rules (must hold byte-for-byte):
 *   - two spaces follow each colon and each comma in the all-values path
 *   - SPH / CYL / ADD: signed, two decimals (+0.75, -3.00); plano is UNSIGNED 0.00
 *   - AXIS: integer, no sign, no zero-pad (88, 3, 180); 'na' -> 'N/A'
 *   - ADD column included ONLY when progressive
 *   - a real CR+LF separates OD from OS, with a single space before and after
 *   - OD = right eye, OS = left eye
 */
(function (global) {
  'use strict';

  // Real \r\n between the OD and OS segments (not a bare \n).
  var CRLF = String.fromCharCode(13) + String.fromCharCode(10);

  // Number -> sign + 2 decimals; 0 -> '0.00' (unsigned). ''/null/NaN -> null.
  function fmtDiopter(val) {
    if (val === '' || val == null) return null;
    var n = parseFloat(val);
    if (isNaN(n)) return null;
    if (n > 0) return '+' + n.toFixed(2);
    if (n < 0) return n.toFixed(2); // toFixed keeps the leading minus
    return '0.00';                  // plano is unsigned
  }

  // Integer axis, no sign, no zero-pad. 'na' -> 'N/A'. ''/null/NaN -> null.
  function fmtAxis(val) {
    if (val === '' || val == null) return null;
    if (val === 'na') return 'N/A';
    var n = parseInt(val, 10);
    if (isNaN(n)) return null;
    return String(n);
  }

  // field('SPH','-3.00') -> 'SPH:  -3.00' (two spaces); empty value -> 'SPH: '
  function field(label, v) { return label + ': ' + (v ? ' ' + v : ''); }

  // rx = { od: {sph,cyl,axis,add}, os: {sph,cyl,axis,add} } with raw input values.
  // isProgressive adds the ADD column. Blank fields render byte-exactly like
  // BCPO's blank format ("(SPH: ,  CYL: ,  AXIS: )") — never throws, never blocks.
  function serializeRx(rx, isProgressive) {
    function seg(eye) {
      var e = (rx && rx[eye]) || {};
      var parts = [
        field('SPH', fmtDiopter(e.sph) || ''),
        field('CYL', fmtDiopter(e.cyl) || ''),
        field('AXIS', fmtAxis(e.axis) || '')
      ];
      if (isProgressive) parts.push(field('ADD', fmtDiopter(e.add) || ''));
      return '(' + parts.join(',  ') + ')';
    }
    return 'OD (Right): ' + seg('od') + ' ' + CRLF + ' OS (Left): ' + seg('os');
  }

  var api = {
    fmtDiopter: fmtDiopter,
    fmtAxis: fmtAxis,
    serializeRx: serializeRx,
    CRLF: CRLF
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  global.BASN5Rx = api;
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this));
